import { createSelector } from "reselect"
import { get, first, last, chain, find } from "lodash"
import moment from "moment"
import { getModelFactors } from "selectors/modelMetadata"
import { getStartDate, getEndDate, getSelectedDate } from "modules/security-profile/selectors/dates"

const defaultExposureData = {}
const getExposureData = state => get(state, "global.data.securityExposureHistory.data.model.simulation.exposure", defaultExposureData)
const getCategory = state => get(state, "modules.securityProfileExposure.displayType")

function getExposureColorClass(value) {
  switch (true) {
    case value < -2:
      return "cold-darker"
    case value < -1.5:
      return "cold-dark"
    case value < -1:
      return "cold-base"
    case value < -0.5:
      return "cold-light"
    case value < 0:
      return "cold-lighter"
    case value > 2:
      return "hot-darker"
    case value > 1.5:
      return "hot-dark"
    case value > 1:
      return "hot-base"
    case value > 0.5:
      return "hot-light"
    case value > 0:
      return "hot-lighter"
    default:
      return ""
  }
}

function getFactorLabel(factors, id) {
  const factor = get(factors, id, { label: id })
  return factor.overviewLabel || factor.label
}

function getFactorCategory(factors, id) {
  return get(factors, [id, "category"])
}

function formatExposure(exposure) {
  return chain(exposure)
    .map(({ date, factors }) => {
      if (factors.length) {
        return ({
          date,
          factors: chain(factors)
            .keyBy("id")
            .mapValues("net")
            .value(),
        })
      }

      return null
    })
    .filter(data => data)
    .value()
}

export const getExposure = createSelector(
  [getExposureData],
  (absolute) =>
    formatExposure(absolute),
)

export const getFilteredExposure = createSelector(
  [getExposure, getStartDate, getEndDate],
  (exposure, startDate, endDate) =>
    exposure.filter((data) => {
      if (data.date >= startDate && data.date <= endDate) {
        return data
      } else {
        return null
      }
    }),
)

export const getFirst = createSelector(
  [getExposure],
  (exposure) => first(exposure),
)

export const getCurrent = createSelector(
  [getExposure],
  (exposure) => last(exposure),
)

export const getFirstHistoricalExposureDate = createSelector(
  [getFilteredExposure],
  (exposure) => get(first(exposure), "date", null),
)

const getSelectedDateFactors = createSelector(
  [getExposureData, getSelectedDate],
  (securityExposure, date) => {
    const history = get(find(securityExposure, d => moment(d.date).isSame(date)), "factors")

    return chain(history)
      .keyBy("id")
      .mapValues(item => ({ value: item.net }))
      .value()
  },
)

export const getExposureAttributionData = createSelector(
  [getSelectedDateFactors, getCategory, getModelFactors],
  (selectedDateFactors, category, factorsMetadata) =>
    chain(selectedDateFactors)
      .map((factor, id) => ({
        id,
        className: getExposureColorClass(factor.value),
        value: factor.value,
        label: getFactorLabel(factorsMetadata, id),
        category: getFactorCategory(factorsMetadata, id),
      }))
      .filter(factor =>
        !category || category === "total" || category === "factors" || category === factor.category,
      )
      .reduce((accumulator, factor) => {
        if (factor.value > 0) {
          accumulator.positive.push(factor)
        } else if (factor.value < 0) {
          accumulator.negative.push(factor)
        }

        return accumulator
      }, { positive: [], negative: [] })
      .mapValues(factors =>
        chain(factors)
          .sortBy(factor => -Math.abs(factor.value))
          .take(10)
          .value(),
      )
      .value(),
)

export const getMaxFactors = createSelector(
  [getExposureAttributionData],
  (exposureAttribution) => {
    const topPositive = get(exposureAttribution, "positive[0].value", 0)
    const topNegative = get(exposureAttribution, "negative[0].value", 0)
    return Math.max(topPositive, Math.abs(topNegative))
  },
)
